/**
 * INTERNAL 这一路 OTA 的 logo 解析。
 *
 * INTERNAL 是"酒店自有渠道"（内部问卷），不像 Booking / Agoda 那样有自己的品牌标识 ——
 * 它的标应该跟着酒店所属集团走。目前的规则：
 *
 *   hotelGroup === 'Marriott'  →  marriott.png / icon-marriott
 *   其它（含取不到 hotelGroup）→  internal.png / icon-internal
 *
 * ⚠️ 必须在 computed / method 里调用，不要在 data() 里求值。
 *    router-view 的 key 是路由 path（见 layout/components/AppMain.vue），同一个路由下换酒店
 *    组件不会重建，data() 里算一次的话换到别的集团 logo 不会跟着变。
 */

const INTERNAL_LOGO = require("@/assets/platforms/internal.png");
const MARRIOTT_LOGO = require("@/assets/platforms/marriott.png");

/** 目前只有 Marriott 一条特例；以后要加集团就往这张表里加，别在各个组件里散着判断 */
const GROUP_LOGOS = {
  marriott: { png: MARRIOTT_LOGO, sprite: "icon-marriott" },
};

const DEFAULT_LOGO = { png: INTERNAL_LOGO, sprite: "icon-internal" };

function groupKey(hotelGroup) {
  return String(hotelGroup == null ? "" : hotelGroup).trim().toLowerCase();
}

/** 从 vuex 取当前酒店的集团名；拿不到当前酒店时返回空串（走默认标） */
export function currentHotelGroup(store) {
  const hotel = store && store.getters && store.getters["hotel/currentHotel"];
  return (hotel && hotel.hotelGroup) || "";
}

/**
 * INTERNAL 的位图 logo。
 * @param {Object} store vuex store（组件里传 this.$store）
 */
export function internalLogo(store) {
  return (GROUP_LOGOS[groupKey(currentHotelGroup(store))] || DEFAULT_LOGO).png;
}

/**
 * INTERNAL 的 SVG 雪碧图 symbol id（不带 #）。
 * @param {Object} store vuex store
 */
export function internalSpriteId(store) {
  return (GROUP_LOGOS[groupKey(currentHotelGroup(store))] || DEFAULT_LOGO).sprite;
}

// ── INTERNAL 的显示名称（同样跟着集团走）─────────────────────────────────────
// 默认用各处原来的名称（i18n 的 OTAs.INTERNAL = "Internal"）；IHG 集团的酒店改叫 "Direct"。
// hotelGroup 的真实取值没在代码里写死过，这里宽松匹配：集团名包含 ihg / intercontinental 都算 IHG。
// 以后要给别的集团改名，往 GROUP_INTERNAL_NAMES 里加一条即可。
const GROUP_INTERNAL_NAMES = [
  { match: (key) => key.includes("ihg") || key.includes("intercontinental"), name: "Direct" },
];

/**
 * 集团名是否属于 IHG（和上面 Direct 改名同一套宽松匹配）。
 * @param {string} hotelGroup
 */
export function isIhgGroup(hotelGroup) {
  const key = groupKey(hotelGroup);
  return !!key && GROUP_INTERNAL_NAMES[0].match(key);
}

/**
 * INTERNAL 在当前酒店集团下的显示名。
 * @param {Object} store vuex store
 * @param {string} fallback 没有命中特例时用的名称（调用方原来显示的那个）
 */
export function internalName(store, fallback = "Internal") {
  const key = groupKey(currentHotelGroup(store));
  const hit = key && GROUP_INTERNAL_NAMES.find((g) => g.match(key));
  return hit ? hit.name : fallback;
}

/**
 * 通用：某个来源的显示名。只有 INTERNAL 会按集团改名，其余原样返回 fallback。
 * 同样要在 computed / method 里调用（原因见文件头）。
 * @param {string} code  来源代码，大小写都行（INTERNAL / Internal / internal）
 * @param {string} fallback 调用方原本要显示的名称
 * @param {Object} store vuex store
 */
export function otaDisplayName(code, fallback, store) {
  if (String(code || "").toUpperCase() !== "INTERNAL") return fallback;
  return internalName(store, fallback);
}

/**
 * 把一张"OTA code → 位图"的静态表里的 INTERNAL 换成当前集团的标，其余原样返回。
 * 组件里这么用：
 *   computed: { otaImgResolved() { return withInternalLogo(this.otaImg, this.$store); } }
 */
export function withInternalLogo(map, store) {
  if (!map || !Object.prototype.hasOwnProperty.call(map, "INTERNAL")) return map;
  return Object.assign({}, map, { INTERNAL: internalLogo(store) });
}
